import { useRef, useState } from 'react';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Box,
  Button,
  ClickAwayListener,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grow,
  IconButton,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Tooltip,
  Typography,
} from '@mui/material';
import { ClearAttributeMappingsPayload } from 'services/sheet-attribute-manager/models/sheet-attribute-manager.interface';
import { MappingActionsToolbarProps } from './MappingActionsToolbar.props';
import {
  createLabelsButtonSx,
  createLabelsTextSx,
  popperSx,
  pushMappingsButtonSx,
  toolbarContainerSx,
} from './MappingActionsToolbar.styles';
import { XLSX_VIEWER_STRINGS } from './xlsx-viewer.contants';

const MappingActionsToolbar = ({
  documentReferenceId,
  activeSheetLabel,
  onPushMappings,
  onClearMappings,
  onReprocessDocument,
  primaryReprocessLabel,
  showCreateLabels = true,
  showPushMappings = false,
}: MappingActionsToolbarProps) => {
  const [open, setOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingClearPayload, setPendingClearPayload] = useState<ClearAttributeMappingsPayload>();
  const anchorRef = useRef<HTMLButtonElement>(null);

  const handleToggle = () => {
    setOpen(prevOpen => !prevOpen);
  };

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }
    setOpen(false);
  };

  const handleOpenConfirmDialog = (payload?: ClearAttributeMappingsPayload) => {
    setPendingClearPayload(payload);
    setConfirmDialogOpen(true);
    setOpen(false);
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
    setPendingClearPayload(undefined);
  };

  const handleConfirmClearMappings = () => {
    onClearMappings(pendingClearPayload);
    handleCloseConfirmDialog();
  };

  const handleCreateLabels = () => {
    onReprocessDocument?.();
    setOpen(false);
  };

  return (
    <>
      <Box sx={toolbarContainerSx}>
        {/* CREATE LABELS button */}
        {showCreateLabels && (
          <Button variant="text" size="small" onClick={handleCreateLabels} sx={createLabelsButtonSx}>
            <Typography component="span" sx={createLabelsTextSx}>
              {primaryReprocessLabel ?? XLSX_VIEWER_STRINGS.BUTTON.CREATE_LABELS}
            </Typography>
          </Button>
        )}

        {showPushMappings && (
          <Tooltip title="Push Mappings">
            <Button
              variant="contained"
              color="success"
              size="small"
              onClick={onPushMappings}
              sx={pushMappingsButtonSx}>
              {XLSX_VIEWER_STRINGS.BUTTON.PUSH_MAPPINGS}
            </Button>
          </Tooltip>
        )}

        {/* CLEAR MAPPINGS hamburger menu */}
        <Tooltip title={XLSX_VIEWER_STRINGS.MENU.CLEAR_MENU_TOOLTIP}>
          <IconButton
            ref={anchorRef}
            size="small"
            onClick={handleToggle}
            aria-controls={open ? XLSX_VIEWER_STRINGS.ARIA.MENU_ID : undefined}
            aria-expanded={open ? 'true' : undefined}
            aria-haspopup="menu">
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* DROPDOWN MENU */}
      <Popper
        sx={popperSx}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        placement="bottom-end">
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
            }}>
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList autoFocusItem id={XLSX_VIEWER_STRINGS.ARIA.MENU_ID}>
                  <MenuItem
                    onClick={() =>
                      handleOpenConfirmDialog({
                        documentReferenceId,
                        sheet: `${activeSheetLabel}`,
                      })
                    }>
                    {XLSX_VIEWER_STRINGS.MENU.CLEAR_SHEET} {activeSheetLabel ? `(${activeSheetLabel})` : ''}{' '}
                    {XLSX_VIEWER_STRINGS.MENU.CLEAR_SHEET_SUFFIX}
                  </MenuItem>
                  <MenuItem onClick={() => handleOpenConfirmDialog({ documentReferenceId })}>
                    {XLSX_VIEWER_STRINGS.MENU.CLEAR_DOCUMENT}
                  </MenuItem>
                  <MenuItem onClick={() => handleOpenConfirmDialog()}>{XLSX_VIEWER_STRINGS.MENU.CLEAR_ALL}</MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>

      {/* Confirmation dialog for clear actions */}
      <Dialog open={confirmDialogOpen} onClose={handleCloseConfirmDialog}>
        <DialogTitle>{XLSX_VIEWER_STRINGS.DIALOG.TITLE}</DialogTitle>
        <DialogContent>
          <DialogContentText>{XLSX_VIEWER_STRINGS.DIALOG.CONTENT}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} color="primary">
            {XLSX_VIEWER_STRINGS.DIALOG.CANCEL}
          </Button>
          <Button onClick={handleConfirmClearMappings} color="error" variant="contained">
            {XLSX_VIEWER_STRINGS.DIALOG.CONFIRM}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MappingActionsToolbar;
